// Product Details Section - Full-width product details page
// Follows the Section, Component & Manager System architecture

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../../../../components/Layout';
import { ProductDetailsManager } from './managers/product-details-manager';
import { useManager } from '../../../../core';
import { useAuth } from '../../../../contexts/AuthContext';
import { ProductDetailsLayout } from './components/ProductDetailsLayout';
import { ProductSearchBar } from '../../components/ProductSearchBar';
import './ProductDetailsSection.css';
import '../../ProductsSection.css';

export function ProductDetailsSection() {
  const { manager, isInitialized } = useManager(ProductDetailsManager);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  /** Revolt Desktop (updater channel) download rows are visible to everyone on public product details. */
  const canViewRevoltDesktopDownloads = true;

  // React state for product data
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [buyLoading, setBuyLoading] = useState(false);

  // Search state for details search bar
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const latestSearchRequestRef = useRef(0);
  const activeProductIdRef = useRef('');
  const latestSearchTermRef = useRef('');

  // Get productId and session_id from URL parameters
  const productId = searchParams.get('productId') || '';
  const sessionId = searchParams.get('session_id') || '';


  // Perform real search API call
  const performSearch = useCallback(async (query, currentProductId) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const requestId = ++latestSearchRequestRef.current;

    try {
      const response = await manager.searchProductsFromAPI(query, 1, 7); // Limit to 7 results
      let results = response.products || [];

      // Filter out the current product we're viewing (don't show it in search results)
      results = results.filter(product => product.id !== currentProductId);

      // Ignore stale responses from previous requests/transitions
      if (requestId !== latestSearchRequestRef.current) return;
      if (activeProductIdRef.current !== currentProductId) return;
      if (latestSearchTermRef.current !== query) return;

      setSearchResults(results);
    } catch (error) {
      if (requestId !== latestSearchRequestRef.current) return;
      console.error('Search failed:', error);
      setSearchResults([]);
    }
  }, [manager]);

  // Handle search input changes (immediate UI update)
  const handleSearchChange = useCallback((newTerm) => {
    setSearchTerm(newTerm);
    // Debounce and dropdown display is handled by ProductSearchBar component
  }, []);

  // Handle navigation to main products page
  const handleProductsClick = useCallback((e) => {
    e.preventDefault();
    navigate('/marketplace');
  }, [navigate]);

  // Handle navigation to admin version of this details page
  const handleAdminClick = useCallback((e) => {
    e.preventDefault();
    navigate(`/marketplace/admin/details?productId=${productId}`);
  }, [navigate, productId]);

  // Handle product click in bundles/related - navigate to that product's details
  const handleProductClick = useCallback((p) => {
    if (p?.id) navigate(`/marketplace/details?productId=${p.id}`);
  }, [navigate]);

  // Handle Buy button - create Stripe checkout and redirect
  const handleBuyClick = useCallback(async (p) => {
    if (!p?.id || !manager) return;
    setBuyLoading(true);
    try {
      await manager.createProductCheckout(p.id);
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
      setBuyLoading(false);
    }
  }, [manager]);

  // Memoize loadProductData to prevent unnecessary re-renders
  const loadProductData = useCallback(async (id) => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const productData = await manager.getProductById(id);
      setProduct(productData);
    } catch (error) {
      console.error('Failed to load product:', error);
      setError(error.message || 'Failed to load product');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [manager]);

  // Handle session_id from Stripe redirect - fetch session to trigger backend processing
  const fetchedSessionRef = useRef(new Set());
  useEffect(() => {
    if (!isInitialized || !sessionId || !user) return;
    if (fetchedSessionRef.current.has(sessionId)) return;
    fetchedSessionRef.current.add(sessionId);

    fetch(`/api/stripe/checkout-session/${sessionId}`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.session?.payment_status === 'paid') {
          setPurchaseSuccess(true);
          refreshUser?.();
          // Refetch product to get hasPurchased: true for Purchased badge
          loadProductData(productId);
          // Remove session_id from URL without full reload
          navigate(`/marketplace/details?productId=${productId}`, { replace: true });
        }
      })
      .catch((err) => console.error('Failed to fetch checkout session:', err));
  }, [isInitialized, sessionId, user, navigate, productId, refreshUser, loadProductData]);

  // Handle hard search (Enter key - navigate to main products page)
  const handleSearchSubmit = useCallback((term) => {
    if (term.trim()) {
      // Navigate to main products page with search query
      const params = new URLSearchParams();
      params.set('query', term.trim());
      window.location.href = `/marketplace?${params.toString()}`;
    }
  }, []);

  // Load product when productId changes
  useEffect(() => {
    activeProductIdRef.current = productId || '';
    latestSearchTermRef.current = '';
    latestSearchRequestRef.current += 1;

    if (isInitialized && productId) {
      // Clear search state immediately when navigating to prevent race conditions
      setSearchTerm('');
      setSearchResults([]);
      loadProductData(productId);
    } else {
      setProduct(null);
      setLoading(false);
      setError(null);
      setSearchTerm('');
      setSearchResults([]);
    }
  }, [isInitialized, productId, loadProductData]);

  // Perform search when searchTerm changes (debounced by ProductSearchBar)
  useEffect(() => {
    if (!isInitialized || !productId || loading) return;

    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
      latestSearchTermRef.current = '';
      latestSearchRequestRef.current += 1;
      setSearchResults([]);
      return;
    }

    latestSearchTermRef.current = trimmedTerm;
    performSearch(trimmedTerm, productId);
  }, [isInitialized, productId, loading, searchTerm, performSearch]);

  return (
    <MainLayout contentClassName="main-content--product-details">
      <div className="product-details-section">
        {/* Search Bar Row */}
        <div className="product-details-search-row">
          <div className="product-details-controls-row">
            {user && user.role === 'admin' && (
              <button
                className="products-admin-btn"
                onClick={handleAdminClick}
                title="Manage Product (Admin)"
              >
                <i className="fa-solid fa-cog"></i>
                Admin
              </button>
            )}
            <a
              href="/marketplace"
              className="product-details-title-inline"
              onClick={handleProductsClick}
            >
              Marketplace
            </a>
            <ProductSearchBar
              searchTerm={searchTerm}
              onSearchChange={handleSearchChange}
              onSubmit={handleSearchSubmit}
              placeholder="Search products..."
              context="details"
              searchResults={searchResults}
            />
          </div>
        </div>

        {/* Product Gallery and Details Content Container - max-width 1400px */}
        <div className="product-details-content">
          {loading ? (
            <div className="product-details-loading-state">
              <div className="product-details-loading-content">
                <i className="fa-solid fa-spinner fa-spin"></i>
                <span>Loading product...</span>
              </div>
            </div>
          ) : error ? (
            <div className="product-details-error-state">
              <div className="product-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Failed to load product</h3>
                <p>{error}</p>
                <p><small>Product ID: {productId}</small></p>
              </div>
            </div>
          ) : product ? (
            <>
              {purchaseSuccess && (
                <div className="product-purchase-success" style={{ marginBottom: '16px', padding: '12px 16px', background: 'rgba(46, 204, 113, 0.15)', border: '1px solid rgba(46, 204, 113, 0.4)', borderRadius: '8px', color: '#27ae60' }}>
                  <i className="fa-solid fa-check-circle"></i> Purchase successful! Thank you for your order.
                </div>
              )}
              <ProductDetailsLayout
                product={product}
                admin={false}
                canViewRevoltDesktopDownloads={canViewRevoltDesktopDownloads}
                manager={manager}
                onProductClick={handleProductClick}
                onBuyClick={user ? handleBuyClick : undefined}
                buyLoading={buyLoading}
              />
            </>
          ) : (
            <div className="product-details-error-state">
              <div className="product-details-error-content">
                <i className="fa-solid fa-exclamation-triangle"></i>
                <h3>Product not found</h3>
                <p>The requested product could not be found.</p>
                <p><small>Product ID: {productId}</small></p>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}